import { apiClient } from '@/lib/api-client';
import type { Invoice, LineItem, PaymentMethod } from '@/types';

export interface InvoiceFilters {
  clientId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export interface CreateInvoiceRequest {
  clientId: string;
  projectId?: string;
  issueDate: string;
  dueDate: string;
  servicePeriodStart?: string;
  servicePeriodEnd?: string;
  lineItems: Omit<LineItem, 'id'>[];
  taxRate?: number;
  discount?: number;
  notes?: string;
  termsAndConditions?: string;
}

export interface UpdateInvoiceRequest {
  clientId?: string;
  projectId?: string;
  issueDate?: string;
  dueDate?: string;
  servicePeriodStart?: string;
  servicePeriodEnd?: string;
  lineItems?: Omit<LineItem, 'id'>[];
  taxRate?: number;
  discount?: number;
  notes?: string;
  termsAndConditions?: string;
}

export interface MarkInvoiceAsPaidRequest {
  paidAmount: number;
  paidDate: string;
  paymentMethod: PaymentMethod;
}

export interface GeneratePdfResult {
  pdfUrl: string;
  pdfKey: string;
}

export interface NextInvoiceNumberResponse {
  invoiceNumber: string;
}

export class InvoicesService {
  /**
   * Get all invoices with optional filters
   */
  async getInvoices(filters?: InvoiceFilters): Promise<Invoice[]> {
    const params = new URLSearchParams();
    if (filters?.clientId) params.append('clientId', filters.clientId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const queryString = params.toString();
    const url = queryString ? `/invoices?${queryString}` : '/invoices';

    return await apiClient.get<Invoice[]>(url);
  }

  /**
   * Get a specific invoice by ID
   */
  async getInvoice(id: string): Promise<Invoice> {
    return await apiClient.get<Invoice>(`/invoices/${id}`);
  }

  /**
   * Create a new invoice
   */
  async createInvoice(data: CreateInvoiceRequest): Promise<Invoice> {
    return await apiClient.post<Invoice>('/invoices', data);
  }

  /**
   * Update an existing invoice (draft only)
   */
  async updateInvoice(id: string, data: UpdateInvoiceRequest): Promise<Invoice> {
    return await apiClient.put<Invoice>(`/invoices/${id}`, data);
  }

  /**
   * Delete an invoice (draft only)
   */
  async deleteInvoice(id: string): Promise<void> {
    return await apiClient.delete<void>(`/invoices/${id}`);
  }

  /**
   * Send invoice (change status from draft to sent)
   */
  async sendInvoice(id: string): Promise<Invoice> {
    return await apiClient.post<Invoice>(`/invoices/${id}/send`, {});
  }

  /**
   * Mark invoice as paid
   */
  async markInvoiceAsPaid(id: string, data: MarkInvoiceAsPaidRequest): Promise<Invoice> {
    return await apiClient.post<Invoice>(`/invoices/${id}/pay`, data);
  }

  /**
   * Alias for markInvoiceAsPaid
   */
  async payInvoice(id: string, data: MarkInvoiceAsPaidRequest): Promise<Invoice> {
    return this.markInvoiceAsPaid(id, data);
  }

  /**
   * Cancel invoice
   */
  async cancelInvoice(id: string): Promise<Invoice> {
    return await apiClient.post<Invoice>(`/invoices/${id}/cancel`, {});
  }

  /**
   * Set or clear the "sent to accounting" flag on an invoice
   */
  async setSentToAccounting(id: string, sentToAccounting: boolean): Promise<Invoice> {
    return await apiClient.post<Invoice>(`/invoices/${id}/accounting`, { sentToAccounting });
  }

  /**
   * Generate PDF for invoice
   */
  async generateInvoicePdf(id: string): Promise<GeneratePdfResult> {
    return await apiClient.get<GeneratePdfResult>(`/invoices/${id}/pdf`);
  }

  /**
   * Get next available invoice number
   */
  async getNextInvoiceNumber(): Promise<string> {
    const response = await apiClient.get<NextInvoiceNumberResponse>('/invoices/next-number');
    return response.invoiceNumber;
  }
}

export const invoicesService = new InvoicesService();
